import { useState, useEffect } from "react";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Calendar } from "primereact/calendar";
import { Button } from "primereact/button";
import vehicleService from "../../services/vehicles/vehicleservice";

interface AddVehicleProps {
  vehicleId?: number | null;
  onSaved?: () => void;
  showToast: (msg: string, severity: "success" | "error") => void;
}

const AddVehicle = ({ vehicleId, onSaved, showToast }: AddVehicleProps) => {

  let searchTimer: any = null;

  const [vehicle, setVehicle] = useState({
    id: "",
    vehicleNumber: "",
    vehicleTypeId: null as number | null,
    vehicleOwnershipType: null as string | null,

    posid: null as number | null,
    posCode: "",

    manufacturer: "",
    model: "",
    yearOfManufacture: null as number | null,

    engineNumber: "",
    chassisNumber: "",
    fuelType: "",
    loadCapacityKg: null as number | null,

    ownerName: "",
    ownerContact: "",

    registrationDate: null as Date | null,
    insuranceExpiryDate: null as Date | null,
    permitNumber: "",
    permitExpiryDate: null as Date | null,
    fcExpiryDate: null as Date | null,
    emissionExpiryDate: null as Date | null,
    vehicleTypeName: "",
    status: true,
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [vehicleTypes, setVehicleTypes] = useState<any[]>([]);
  const [posList, setPosList] = useState<any[]>([]);

  // Load Vehicle Types
  const loadVehicleTypes = async (keyword: string | null) => {
    const data = await vehicleService.getVehicleTypes(keyword);
    const formatted = data.map((x: any) => ({
      label: x.vehicleTypeName,
      value: x.vehicleTypeId,
    }));
    setVehicleTypes(formatted);
  };

  const debounceSearchVehicleTypes = (keyword: string) => {
    if (searchTimer) clearTimeout(searchTimer);
    searchTimer = setTimeout(() => loadVehicleTypes(keyword), 300);
  };

  useEffect(() => {
    loadVehicleTypes("");
    loadPOSList("");
  }, []);

  // Load Vehicle Details (Edit Mode)
  const loadVehicleDetails = async (id: number) => {
    const data = await vehicleService.getVehicleById(id);

    setVehicle({
      ...data,
      registrationDate: data.registrationDate ? new Date(data.registrationDate) : null,
      insuranceExpiryDate: data.insuranceExpiryDate ? new Date(data.insuranceExpiryDate) : null,
      permitExpiryDate: data.permitExpiryDate ? new Date(data.permitExpiryDate) : null,
      fcExpiryDate: data.fcExpiryDate ? new Date(data.fcExpiryDate) : null,
      emissionExpiryDate: data.emissionExpiryDate ? new Date(data.emissionExpiryDate) : null,
    });
  };

  useEffect(() => {
    if (vehicleId) {
      loadVehicleDetails(vehicleId);
    }
  }, [vehicleId]);

  // Handle Inputs
  const handleChange = (field: string, value: any) => {

    // VEHICLE NUMBER VALIDATION
  if (field === "vehicleNumber") {
    // Allow only alphanumeric characters
    const clean = value.toUpperCase().replace(/[^A-Z0-9]/g, "");

    // Must contain at least 1 letter AND 1 digit
    const hasLetter = /[A-Z]/.test(clean);
    const hasDigit = /[0-9]/.test(clean);

    setVehicle((prev) => ({
      ...prev,
      vehicleNumber: clean
    }));

    if (!hasLetter || !hasDigit) {
      setErrors((prev) => ({
        ...prev,
        vehicleNumber: "Vehicle Number must contain both alphabets and numbers"
      }));
    } else {
      setErrors((prev) => ({ ...prev, vehicleNumber: "" }));
    }

    return;
  }
  
    setVehicle((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const isAlphaNumericWithLetterAndNumber = (value: string) => {
  const hasLetter = /[A-Za-z]/.test(value);
  const hasDigit = /[0-9]/.test(value);
  return hasLetter && hasDigit;
};

  // Validation
  const validateForm = () => {
    const newErrors: any = {};

    if (!vehicle.vehicleNumber) {
    newErrors.vehicleNumber = "Vehicle Number is required";
    } 
    else if (!isAlphaNumericWithLetterAndNumber(vehicle.vehicleNumber)) {
      newErrors.vehicleNumber = "Vehicle Number must contain both alphabets and numbers";
    }
    if (!vehicle.vehicleTypeId) newErrors.vehicleTypeId = "Vehicle Type is required";
    if (!vehicle.vehicleOwnershipType) newErrors.vehicleOwnershipType = "Ownership Type is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const toDateOnly = (date: Date | null) => {
  if (!date) return null;

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};


  // Submit
  const handleSubmit = async () => {
    if (!validateForm()) {
      showToast("Please fill required fields", "error");
      return;
    }

    const payload = {
      ...vehicle,
      id: vehicleId,
      registrationDate: toDateOnly(vehicle.registrationDate),
      insuranceExpiryDate: toDateOnly(vehicle.insuranceExpiryDate),
      permitExpiryDate: toDateOnly(vehicle.permitExpiryDate),
      fcExpiryDate: toDateOnly(vehicle.fcExpiryDate),
      emissionExpiryDate: toDateOnly(vehicle.emissionExpiryDate)
    };

    try {
      let response;

      if (vehicleId) {
        response = await vehicleService.updateVehicle(payload);
      } else {
        response = await vehicleService.addVehicle(payload);
      }

      if (response.isSuccess) {
        showToast(response.message, "success");
        onSaved?.();
      } else {
        showToast(response.message, "error");
      }
    } catch {
      showToast("Failed to submit", "error");
    }
  };

  const loadPOSList = async (keyword: string | null) => {
    const data = await vehicleService.getPOSList(keyword);

    const formatted = data.map((x: any) => ({
      label: x.posAlias,
      value: x.posID,
    }));

    setPosList(formatted);
  };

  const debounceSearchPOS = (keyword: string) => {
    if (searchTimer) clearTimeout(searchTimer);
    searchTimer = setTimeout(() => loadPOSList(keyword), 300);
  };

  const isInternal = vehicle.vehicleOwnershipType === "INT";

  return (
    <div className="w-full p-3">

      <div
        className="w-full p-4 border-round-lg"
        style={{ border: "1px solid #d1d5db", background: "#fff", boxShadow: "0 2px 8px rgba(0,0,0,0.08)" }}
      >
        <div
          className="text-center text-white font-semibold mb-4"
          style={{ background: "#0a7d2b", padding: "12px", borderRadius: "6px", fontSize: "18px" }}
        >
          VEHICLE REGISTRATION
        </div>

        <div className="grid w-full">

          {/* Vehicle Number */}
          <div className="col-12 md:col-6">
            <label className="font-medium">Vehicle Number <span className="text-red-500">*</span></label>
            <InputText
              value={vehicle.vehicleNumber ?? ""}
              onChange={(e) => handleChange("vehicleNumber", e.target.value)}
              maxLength={20}
              className="w-full p-inputtext-sm"
              style={{ border: "1px solid #cbd5e1" }}
            />
            {errors.vehicleNumber && <small className="p-error">{errors.vehicleNumber}</small>}
          </div>

          {/* Vehicle Type Dropdown */}
          <div className="col-12 md:col-6">
            <label className="font-medium">Vehicle Type <span className="text-red-500">*</span></label>
            <Dropdown
              value={vehicle.vehicleTypeId}
              options={vehicleTypes}
              onChange={(e) => handleChange("vehicleTypeId", e.value)}
              onFilter={(e) => debounceSearchVehicleTypes(e.filter)}
              filter
              showFilterClear
              placeholder="Search vehicle type..."
              className="w-full"
              style={{ border: "1px solid #cbd5e1" }}
            />
            {errors.vehicleTypeId && <small className="p-error">{errors.vehicleTypeId}</small>}
          </div>

          {/* Ownership Type */}
          <div className="col-12 md:col-6">
            <label className="font-medium">Ownership Type <span className="text-red-500">*</span></label>
            <Dropdown
              value={vehicle.vehicleOwnershipType}
              options={[
                { label: "Internal", value: "INT" },
                { label: "External", value: "EXT" },
              ]}
              onChange={(e) => handleChange("vehicleOwnershipType", e.value)}
              placeholder="Select Ownership"
              className="w-full"
              disabled={!!vehicleId} 
              style={{ border: "1px solid #cbd5e1" }}
            />
            {errors.vehicleOwnershipType && <small className="p-error">{errors.vehicleOwnershipType}</small>}
          </div>

          {/* INTERNAL FIELDS */}
          {isInternal && (
            <>
              <div className="col-12 md:col-6">
                <label className="font-medium">POS ID</label>
                <Dropdown
                  value={vehicle.posid}
                  options={posList}
                  onChange={(e) => handleChange("posid", e.value)}
                  onFilter={(e) => debounceSearchPOS(e.filter)}
                  filter
                  showFilterClear
                  placeholder="Search POS..."
                  className="w-full"
                  style={{ border: "1px solid #cbd5e1" }}
                />
              </div>

              <div className="col-12 md:col-6">
                <label className="font-medium">Manufacturer</label>
                <InputText
                  value={vehicle.manufacturer ?? ""}
                  onChange={(e) => handleChange("manufacturer", e.target.value)}
                  maxLength={50}
                  className="w-full"
                  style={{ border: "1px solid #cbd5e1" }}
                />
              </div>

              <div className="col-12 md:col-6">
                <label className="font-medium">Model</label>
                <InputText
                  value={vehicle.model ?? ""}
                  onChange={(e) => handleChange("model", e.target.value)}
                  maxLength={50}
                  className="w-full"
                  style={{ border: "1px solid #cbd5e1" }}
                />
              </div>

              <div className="col-12 md:col-6">
  <label className="font-medium">Year of Manufacture</label>

  <Calendar
    value={
      vehicle.yearOfManufacture
        ? new Date(vehicle.yearOfManufacture, 0, 1)
        : null
    }
    onChange={(e) => {
      const selectedDate = e.value as Date | null;
      const year = selectedDate ? selectedDate.getFullYear() : null;
      handleChange("yearOfManufacture", year); // store numeric year
    }}
    view="year"
    dateFormat="yy"
    className="w-full"
    style={{ border: "1px solid #cbd5e1" }}
    placeholder="Select Year"
  />
</div>


              <div className="col-12 md:col-6">
                <label className="font-medium">Engine Number</label>
                <InputText
                  value={vehicle.engineNumber ?? ""}
                  onChange={(e) => handleChange("engineNumber", e.target.value)}
                  maxLength={50}
                  className="w-full"
                  style={{ border: "1px solid #cbd5e1" }}
                />
              </div>

              <div className="col-12 md:col-6">
                <label className="font-medium">Chassis Number</label>
                <InputText
                  value={vehicle.chassisNumber ?? ""}
                  onChange={(e) => handleChange("chassisNumber", e.target.value)}
                  maxLength={50}
                  className="w-full"
                  style={{ border: "1px solid #cbd5e1" }}
                />
              </div>

              <div className="col-12 md:col-6">
                <label className="font-medium">Fuel Type</label>
                <Dropdown
                  value={vehicle.fuelType}
                  options={[
                    { label: "Diesel", value: "Diesel" },
                    { label: "Petrol", value: "Petrol" },
                    { label: "CNG", value: "CNG" },
                    { label: "Electric", value: "Electric" },
                  ]}
                  onChange={(e) => handleChange("fuelType", e.value)}
                  placeholder="Select Fuel"
                  className="w-full"
                  style={{ border: "1px solid #cbd5e1" }}
                />
              </div>

              <div className="col-12 md:col-6">
                <label className="font-medium">Load Capacity (KG)</label>
                <InputText
                  value={vehicle.loadCapacityKg?.toString() ?? ""}
                  onChange={(e) => {
                    const onlyDigits = e.target.value.replace(/[^0-9]/g, "");
                    const numericValue = onlyDigits ? Number(onlyDigits) : null;
                    handleChange("loadCapacityKg", numericValue);
                  }}
                  className="w-full"
                  style={{ border: "1px solid #cbd5e1" }}
                />
              </div>

              <div className="col-12 md:col-6">
                <label className="font-medium">Owner Name</label>
                <InputText
                  value={vehicle.ownerName ?? ""}
                  onChange={(e) => handleChange("ownerName", e.target.value)}
                  maxLength={100}
                  className="w-full"
                  style={{ border: "1px solid #cbd5e1" }}
                />
              </div>

              <div className="col-12 md:col-6">
                <label className="font-medium">Owner Contact</label>
                <InputText
                  value={vehicle.ownerContact ?? ""}
                  onChange={(e) => handleChange("ownerContact", e.target.value)}
                  maxLength={15}
                  className="w-full"
                  style={{ border: "1px solid #cbd5e1" }}
                />
              </div>

              <div className="col-12 md:col-6">
                <label className="font-medium">Registration Date</label>
                <Calendar
                  value={vehicle.registrationDate}
                  onChange={(e) => handleChange("registrationDate", e.value)}
                  dateFormat="dd/mm/yy"
                  className="w-full"
                  inputClassName="w-full"
                  style={{ border: "1px solid #cbd5e1" }}
                />
              </div>

              <div className="col-12 md:col-6">
                <label className="font-medium">Insurance Expiry Date</label>
                <Calendar
                  value={vehicle.insuranceExpiryDate}
                  onChange={(e) => handleChange("insuranceExpiryDate", e.value)}
                  dateFormat="dd/mm/yy"
                  className="w-full"
                  inputClassName="w-full"
                  style={{ border: "1px solid #cbd5e1" }}
                />
              </div>

              <div className="col-12 md:col-6">
                <label className="font-medium">Permit Number</label>
                <InputText
                  value={vehicle.permitNumber ?? ""}
                  onChange={(e) => handleChange("permitNumber", e.target.value)}
                  maxLength={50}
                  className="w-full"
                  style={{ border: "1px solid #cbd5e1" }}
                />
              </div>

              <div className="col-12 md:col-6">
                <label className="font-medium">Permit Expiry Date</label>
                <Calendar
                  value={vehicle.permitExpiryDate}
                  onChange={(e) => handleChange("permitExpiryDate", e.value)}
                  dateFormat="dd/mm/yy"
                  className="w-full"
                  inputClassName="w-full"
                  style={{ border: "1px solid #cbd5e1" }}
                />
              </div>

              <div className="col-12 md:col-6">
                <label className="font-medium">FC Expiry Date</label>
                <Calendar
                  value={vehicle.fcExpiryDate}
                  onChange={(e) => handleChange("fcExpiryDate", e.value)}
                  dateFormat="dd/mm/yy"
                  className="w-full"
                  inputClassName="w-full"
                  style={{ border: "1px solid #cbd5e1" }}
                />
              </div>

              <div className="col-12 md:col-6">
                <label className="font-medium">Emission Expiry Date</label>
                <Calendar
                  value={vehicle.emissionExpiryDate}
                  onChange={(e) => handleChange("emissionExpiryDate", e.value)}
                  dateFormat="dd/mm/yy"
                  className="w-full"
                  inputClassName="w-full"
                  style={{ border: "1px solid #cbd5e1" }}
                />
              </div>
            </>
          )}
        </div>

        <div className="flex justify-content-end gap-3 mt-4">
          <Button
            label="Submit"
            icon="pi pi-check"
            className="p-button-success"
            onClick={handleSubmit}
          />
        </div>
      </div>
    </div>
  );
};

export default AddVehicle;
